if (!customElements.get('tier-offers')) {
  customElements.define(
    'tier-offers',
    class TierOffers extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.radios = this.querySelectorAll('input[type="radio"]');
        this.cards = this.querySelectorAll('.tier-card');
        this.variants = [];
        this.prices = {};

        const variantsScript = this.querySelector('.tier-offers__variants-json');
        if (variantsScript) {
          try {
            this.variants = JSON.parse(variantsScript.textContent.trim());
          } catch (e) {
            console.warn('[tier-offers] Could not parse variants JSON:', e);
          }
        }

        const pricesScript = this.querySelector('.tier-offers__prices-json');
        if (pricesScript) {
          try {
            this.prices = JSON.parse(pricesScript.textContent.trim());
          } catch (e) {
            console.warn('[tier-offers] Could not parse prices JSON:', e);
          }
        }

        this.moneyFormat = this.dataset.moneyFormat || window.theme?.moneyFormat || '{{amount}}';
        this.currencySymbol = this.dataset.currencySymbol || window.theme?.currencySymbol || '';
      }

      connectedCallback() {
        this.radios.forEach((radio) => {
          radio.addEventListener('change', this.onRadioChange.bind(this));
        });

        // Sync initial variant selection to form inputs without overwriting server-rendered price
        this.syncInitialSelection();

        // Listen for form submissions to ensure variant and quantity 1
        document.addEventListener('submit', this.onFormSubmit.bind(this), true);

        // Listen for external variant changes
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onExternalVariantChange.bind(this)
          );
        }
      }

      disconnectedCallback() {
        if (this.unsubscribeVariantChange) {
          this.unsubscribeVariantChange();
        }
      }

      syncInitialSelection() {
        const checkedRadio = this.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          this.updateCardStyles(checkedRadio);
          const variantId = checkedRadio.dataset.variantId || checkedRadio.value;
          this.selectVariant(variantId, checkedRadio, false);
        }
      }

      onRadioChange(event) {
        const target = event.target;
        this.updateCardStyles(target);
        const variantId = target.dataset.variantId || target.value;
        this.selectVariant(variantId, target, true);
      }

      updateCardStyles(selectedRadio) {
        this.cards.forEach((card) => {
          const radio = card.querySelector('input[type="radio"]');
          if (radio === selectedRadio) {
            card.classList.add('is-selected');
          } else {
            card.classList.remove('is-selected');
          }
        });
      }

      selectVariant(variantId, sourceRadio = null, shouldUpdatePrice = true) {
        if (!variantId) return;

        const variant = this.variants.find((v) => String(v.id) === String(variantId));
        const sectionContainer = this.closest('.product, .product-section, .shopify-section, [data-section]') || document;

        // 1. Update all variant ID inputs in forms
        const forms = sectionContainer.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
        forms.forEach((form) => {
          let idInput = form.querySelector('input[name="id"]');
          if (idInput) {
            idInput.value = variantId;
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          let qtyInput = form.querySelector('input[name="quantity"]');
          if (!qtyInput) {
            qtyInput = document.createElement('input');
            qtyInput.type = 'hidden';
            qtyInput.name = 'quantity';
            form.appendChild(qtyInput);
          }
          qtyInput.value = 1;
        });

        // 2. Sync with native variant-selects if present
        const variantSelects = sectionContainer.querySelector('variant-selects');
        if (variantSelects && variant && Array.isArray(variant.options)) {
          variant.options.forEach((optValue) => {
            const radioInput = variantSelects.querySelector(`input[type="radio"][value="${CSS.escape(optValue)}"]`);
            if (radioInput && !radioInput.checked) {
              radioInput.checked = true;
              radioInput.dispatchEvent(new Event('change', { bubbles: true }));
            }

            const selectOption = variantSelects.querySelector(`select option[value="${CSS.escape(optValue)}"]`);
            if (selectOption && selectOption.parentElement) {
              selectOption.parentElement.value = optValue;
              selectOption.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        // 3. Update main section price element (#price-sectionId)
        if (shouldUpdatePrice) {
          this.updateSectionPrice(variant, sourceRadio, sectionContainer);
        }

        // 4. Update URL if on product template (on user interaction)
        if (shouldUpdatePrice && window.location.pathname.includes('/products/')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', url.toString());
        }

        // 5. Publish PUB_SUB variant change event
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && variant) {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              variant: variant,
            },
          });
        }

        // 6. Dispatch custom event
        this.dispatchEvent(
          new CustomEvent('tier:change', {
            bubbles: true,
            detail: {
              variantId: variantId,
              variant: variant,
            },
          })
        );
      }

      updateSectionPrice(variant, sourceRadio, sectionContainer) {
        const priceContainer = sectionContainer.querySelector(`#price-${this.sectionId}`) || sectionContainer.querySelector('.price');
        if (!priceContainer) return;

        const variantId = String(variant ? variant.id : sourceRadio?.dataset.variantId || '');
        const priceData = this.prices[variantId];

        const priceFormatted = sourceRadio?.dataset.priceFormatted || priceData?.price_formatted;
        const compareFormatted = sourceRadio?.dataset.comparePriceFormatted || priceData?.compare_at_price_formatted;

        const priceNum = variant ? variant.price : parseInt(sourceRadio?.dataset.price, 10);
        const compareNum = variant ? variant.compare_at_price : parseInt(sourceRadio?.dataset.comparePrice, 10);
        const isOnSale = Boolean(compareFormatted && compareNum && compareNum > priceNum);

        const priceEl = priceContainer.querySelector('.price');
        if (priceEl) {
          if (isOnSale) {
            priceEl.classList.add('price--on-sale');
          } else {
            priceEl.classList.remove('price--on-sale');
          }
        }

        // Update sale block
        const salePriceItem = priceContainer.querySelector('.price__sale .price-item--sale, .price__sale .price-item--last');
        const saleCompareItem = priceContainer.querySelector('.price__sale .price-item--regular s, .price__sale s.price-item--regular');
        if (salePriceItem && priceFormatted) salePriceItem.textContent = priceFormatted;
        if (saleCompareItem && compareFormatted) saleCompareItem.textContent = compareFormatted;

        // Update regular block
        const regPriceItem = priceContainer.querySelector('.price__regular .price-item--regular');
        if (regPriceItem && priceFormatted) regPriceItem.textContent = priceFormatted;
      }

      onExternalVariantChange(event) {
        if (!event || !event.data || !event.data.variant) return;
        const variantId = String(event.data.variant.id);

        const matchingRadio = this.querySelector(`input[type="radio"][data-variant-id="${variantId}"], input[type="radio"][value="${variantId}"]`);
        if (matchingRadio && !matchingRadio.checked) {
          matchingRadio.checked = true;
          this.updateCardStyles(matchingRadio);
        }
      }

      onFormSubmit(event) {
        const form = event.target;
        if (!form || !form.matches || !form.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')) return;

        const checkedRadio = this.querySelector('input[type="radio"]:checked');
        if (!checkedRadio) return;

        const variantId = checkedRadio.dataset.variantId || checkedRadio.value;
        let idInput = form.querySelector('input[name="id"]');
        if (idInput && variantId) {
          idInput.value = variantId;
        }

        let qtyInput = form.querySelector('input[name="quantity"]');
        if (qtyInput) {
          qtyInput.value = 1;
        }
      }

      formatMoney(cents, format) {
        if (typeof cents === 'string') {
          cents = cents.replace('.', '');
        }
        let value = '';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const formatString = format || this.moneyFormat || window.theme?.moneyFormat || '{{amount}}';

        function defaultOption(opt, def) {
          return typeof opt === 'undefined' ? def : opt;
        }

        function formatWithDelimiters(number, precision, thousands, decimal) {
          precision = defaultOption(precision, 2);
          thousands = defaultOption(thousands, ',');
          decimal = defaultOption(decimal, '.');

          if (isNaN(number) || number == null) {
            return 0;
          }

          number = (number / 100.0).toFixed(precision);

          const parts = number.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          const centsPart = parts[1] ? decimal + parts[1] : '';

          return dollars + centsPart;
        }

        if (placeholderRegex.test(formatString)) {
          switch (formatString.match(placeholderRegex)[1]) {
            case 'amount':
              value = formatWithDelimiters(cents, 2);
              break;
            case 'amount_no_decimals':
              value = formatWithDelimiters(cents, 0);
              break;
            case 'amount_with_comma_separator':
              value = formatWithDelimiters(cents, 2, '.', ',');
              break;
            case 'amount_no_decimals_with_comma_separator':
              value = formatWithDelimiters(cents, 0, '.', ',');
              break;
            case 'amount_with_apostrophe_separator':
              value = formatWithDelimiters(cents, 2, "'", '.');
              break;
            default:
              value = formatWithDelimiters(cents, 2);
          }
          return formatString.replace(placeholderRegex, value);
        }

        const sym = this.currencySymbol || '';
        return sym ? `${sym}${formatWithDelimiters(cents, 2)}` : formatWithDelimiters(cents, 2);
      }
    }
  );
}
